import { IPropsWithChildren } from '@omaze/app';
import merge from 'lodash.merge';
import React, { MutableRefObject, ReactElement, useRef, useState } from 'react';
import Slider, { ResponsiveObject, Settings } from 'react-slick';

import { deviceBreakpoints, useMediaBreakpoint } from '../../pages/homepage/shared/hooks/useMediaBreakpoint';

import styles from './Carousel.styles.css';
import { CarouselContext, ICarouselContext } from './CarouselContext';
import { Content } from './Content/Content';
import { Header } from './Header/Header';
import { Item } from './Item/Item';
import { Pagination } from './Pagination/Pagination';

interface ICarouselProps extends IPropsWithChildren {
    /**
     * Will be merged with default settings. Default can be overriden here
     */
    settings?: Settings;
    /**
     * Array of items that to be displayed
     */
    items: any[];
    /**
     * Callback before Carousel changes
     */
    onBeforeChange?: (currentSlide?: number, nextSlide?: number) => void;
    /**
     * Callback after Carousel changes
     */
    onAfterChange?: (currentSlide?: number) => void;
    'data-testid'?: string;
}

function Prev (props: { onClick?: () => void }): ReactElement {
    return (
        <button className={`mr-5 absolute z-10 ${styles.arrow} ${styles.prev}`} onClick={props.onClick}>
            <span className="sr-only">previous page</span>
        </button>
    );
}

function Next (props: { onClick?: () =>  void }): ReactElement {
    return (
        <button className={`absolute right-0 z-10 ${styles.arrow} ${styles.next}`} onClick={props.onClick}>
            <span className="sr-only">next page</span>
        </button>
    );
}

const defaultSettings: Settings = {
    waitForAnimate: false, // adding this so that tests to click next and previous buttons pass. this seems to be from the way jest or react-slick works
    dots: false,
    speed: 200,
    adaptiveHeight: false,
    className: `-mx-1 relative ${styles.carouselSlider}`,
    variableWidth: false,
    slidesToShow: 4,
    slidesToScroll: 4,
    arrows: true,
    infinite: true,
    prevArrow: <Prev />,
    nextArrow: <Next />,
    // responsiveness is desktop first by default from the library
    responsive: [
        {
            breakpoint: deviceBreakpoints.TABLET,
            settings: {
                variableWidth: false,
                slidesToShow: 3,
                slidesToScroll: 3,
                arrows: true,
                infinite: true,
            },
        },
        {
            breakpoint: deviceBreakpoints.MOBILE,
            settings: {
                variableWidth: true,
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: false,
                infinite: true,
            },
        },
    ],
};

// gets the settings for a particular breakpoint
function getBreakpointSettings (userSettings: Settings, breakpoint: string): Settings {
    const settings: Settings = Object.assign({}, userSettings);

    if (breakpoint === 'mobile' || breakpoint === 'tablet') {
        if (userSettings.responsive) {
            const breakpoints:  ResponsiveObject[] = userSettings.responsive;

            const deviceBreakpoint:  ResponsiveObject | undefined = breakpoints.find((bp: ResponsiveObject): boolean => {
                return bp.breakpoint === deviceBreakpoints[breakpoint.toUpperCase()];
            });

            if (deviceBreakpoint && deviceBreakpoint.settings !== 'unslick') {
                return deviceBreakpoint.settings;
            }
        }
    }

    return settings;
}

export function Carousel (props: ICarouselProps): ReactElement | null {
    const { items, settings: customSettings = {}, onBeforeChange, onAfterChange, 'data-testid': dataTestId }: ICarouselProps = props;
    const [currentPage, setCurrentPage]: [number, React.Dispatch<React.SetStateAction<number>>] = useState<number>(1);
    const slideCount: number = items.length;
    const breakpoint: string = useMediaBreakpoint();
    const settings: Settings = merge(defaultSettings, customSettings);

    settings.beforeChange = function (currentSlide: number, nextSlide: number): void {
        if (onBeforeChange) {
            onBeforeChange(currentSlide, nextSlide);
        }

        setCurrentPage(Math.ceil((nextSlide + 1) / slidesToShow));
    };

    settings.afterChange = function (currentSlide: number): void {
        if (onAfterChange) {
            onAfterChange(currentSlide);
        }
    };

    const breakpointSettings: Settings = getBreakpointSettings(settings, breakpoint);
    const slidesToShow: number = breakpointSettings.slidesToShow || 0;
    const totalPages: number = Math.ceil(slideCount / (slidesToShow));
    const previousPage: number = currentPage < 2 ? 1 : currentPage - 1;
    const nextPage: number = currentPage === totalPages ? 1 : currentPage + 1;
    const sliderRef: MutableRefObject<Slider | null> = useRef<Slider | null>(null);

    function shouldRenderPaginationInfo (): boolean {
        if (totalPages < 2) {
            return false;
        }

        if (breakpoint === 'mobile') {
            return false;
        }

        return true;
    }

    const contextValue: ICarouselContext = {
        paginationInfo: {
            currentPage: currentPage,
            previousPage: previousPage,
            nextPage: nextPage,
            totalPages: totalPages,
            shouldRenderPaginationInfo: shouldRenderPaginationInfo(),
        },
        settings: settings,
        sliderRef: sliderRef,
    };

    if (!items.length) {
        return null;
    }

    return (
        <CarouselContext.Provider value={contextValue}>
            <div className={`mx-auto overflow-hidden pl-3 ${styles.carouselContainer}`} data-testid={dataTestId}>
                {props.children}
            </div>
        </CarouselContext.Provider>
    );
}

Carousel.Header = Header;
Carousel.Pagination = Pagination;
Carousel.Content = Content;
Carousel.Item = Item;
